import { type ReactNode } from 'react'
import { Button } from './Button'
import { Icon } from './Icon'
import type { IconName } from './Icon'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  onConfirm?: () => void
  variant?: 'danger' | 'default'
  icon?: IconName
}

export function Modal({
  open,
  onClose,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  variant = 'default',
  icon,
}: ModalProps) {
  if (!open) return null

  const iconBgColor = variant === 'danger' ? 'bg-red-50' : 'bg-blue-50'
  const iconColor = variant === 'danger' ? 'text-[var(--color-error)]' : 'text-[var(--color-secondary)]'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[var(--color-surface)] rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="text-center mb-6">
          {icon && (
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${iconBgColor} mb-4`}>
              <Icon name={icon} size={32} className={iconColor} />
            </div>
          )}
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
            {title}
          </h2>
          <p className="text-[var(--color-text-muted)] text-sm">
            {children}
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            className={`flex-1 ${variant === 'danger' ? '!bg-[var(--color-error)] hover:!bg-red-600' : ''}`}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
